import {Component, Input, OnInit} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ImagePreviewComponent} from '../../shared';
import {ModalService} from '@services/modal/modal.service';
import {AwsUploaderService} from '@services/awsUploader/aws-uploader.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Select, Store} from '@ngxs/store';
import {DisableSliderLoader, EnableSliderLoader, ShareDeviceFormSelectors} from '../../../../store';
import {filter, map, mergeMap, switchMap, take} from 'rxjs/operators';
import {ISharedDevicePhoto} from '@interfaces/shared-devices';
import {SharedDevicesService} from '@services/shared-devices/shared-devices.service';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-location-slider',
  templateUrl: './location-slider.component.html',
  styleUrls: ['./location-slider.component.scss'],
  providers: [{provide: NG_VALUE_ACCESSOR, multi: true, useExisting: LocationSliderComponent}]
})
export class LocationSliderComponent implements OnInit, ControlValueAccessor {
  @Select(ShareDeviceFormSelectors.isSliderLoader) isLoadingSlides$!: Observable<boolean>;
  @Input() disableAddingRemoving = false;

  @Input() set value(value: ISharedDevicePhoto[]) {
    this.writeValue(value);
  }

  slides: ISharedDevicePhoto[] = [];
  maxSlidesNum: number;
  sliderConfig = {
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: false,
    arrows: false,
    infinite: false,
  };
  disabled = false;
  currentSlide = 1;

  constructor(private domSanitizer: DomSanitizer,
              private dialog: ModalService,
              private awsService: AwsUploaderService,
              private store: Store,
              private translate: TranslateService,
              private shareDeviceService: SharedDevicesService) {
    this.maxSlidesNum = this.store.selectSnapshot(ShareDeviceFormSelectors.getMaxSlidesNum);
  }

  ngOnInit(): void {
  }

  private get activeDeviceId(): number | null {
    return this.store.selectSnapshot(ShareDeviceFormSelectors.getActiveDeviceIdToShare);
  }

  onClick(fileUpload: HTMLInputElement): void {
    this.onTouched();
    fileUpload.click();
  }

  onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      this.uploadFiles(files);
    }
  }

  private uploadFiles(files: FileList): void {
    if (!this.activeDeviceId) return;

    if (this.isSlidesExceed(files)) {
      this.showSlidesExceedError();
      return;
    }

    this.store.dispatch(new EnableSliderLoader());

    // getting the photo urls
    this.awsService.filesUrl$.pipe(
      filter((obj) => Boolean(obj)),
      take(files.length),
      map((value) => {
        return {url: value?.url as string};
      }),
      mergeMap((photo) => this.shareDeviceService.uploadPhotosSharedDevice(photo.url, this.activeDeviceId as number))
    ).subscribe({
      next: (photos) => {
        this.store.dispatch(new DisableSliderLoader());
        this.writeValue(photos.rows);
        this.onChange(photos.rows);
      }, complete: () => {
        this.store.dispatch(new DisableSliderLoader());
      }, error: () => this.store.dispatch(new DisableSliderLoader())
    });

    // uploading photos
    Array.from(files).forEach((file, i) => {
      if (this.isImageSizeExceed(file.size)) return;
      this.awsService.uploadToAWS(file, i).pipe(take(files.length)).subscribe();
    });
  }

  private isSlidesExceed(files: FileList): boolean {
    const currentNum = files.length + this.slides.length;
    return currentNum > this.maxSlidesNum;
  }

  private showSlidesExceedError(): void {
    this.dialog.showMsg('Only ' + this.maxSlidesNum + ' images are allowed to upload',
      400, {cancel: false, closePreviousModal: false});
  }

  onDeleteSlide(photoId: number): void {
    if (!this.activeDeviceId) return;

    this.store.dispatch(new EnableSliderLoader());
    this.shareDeviceService.removeSharedDevicePhoto(this.activeDeviceId, photoId)
      .pipe(
        untilDestroyed(this),
        switchMap(() => this.shareDeviceService.getSharedPhotosByDeviceId(this.activeDeviceId as number))
      ).subscribe({
      next: (photos) => {
        this.store.dispatch(new DisableSliderLoader());
        this.writeValue(photos.rows);
        this.onChange(photos.rows);
      }, complete: () => {
        this.store.dispatch(new DisableSliderLoader());
      }
    });
  }

  private isImageSizeExceed(size: number): boolean {
    const oneMbValue = 1048576;
    const exceed = size > oneMbValue * 40;
    if (exceed) {
      this.translate.get('errors.imgMoreThan40mb')
        .pipe(untilDestroyed(this))
        .subscribe((res: string) => {
          this.dialog.showMsg(res, null, {cancel: false, closePreviousModal: false});
        });
    }
    return exceed;
  }

  openImageOnFullScreen(imageUrl: SafeUrl): void {
    this.dialog.openDialog(ImagePreviewComponent, {maxWidth: '1400px', width: '95vw', height: '95vh', data: {imageUrl}});
  }

  onTouched = () => {
  };

  onChange = (slides: ISharedDevicePhoto[]) => {
  };

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  writeValue(value: ISharedDevicePhoto[]): void {
    if (!value) this.slides = [];
    else this.slides = value;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  slickInit($event: { event: any; slick: any }): void {
    this.currentSlide = $event.slick.currentSlide + 1;
  }

  afterSlideChange($event: { event: any; slick: any; currentSlide: number; first: boolean; last: boolean }): void {
    this.currentSlide = $event.currentSlide + 1;
  }
}
